import { Morpheme, Word } from '@/common/types/scopeAndSequence';
import {
  playIntoAudio,
  playTurnTheWordAudio,
  playWordAudio,
  soundOutWord,
} from '@/common/utils/audio';
import { getDistinctMorphemes, weightedSample } from '@/common/utils/helpers';
import { getWordGraphemes } from '@/common/utils/scopeAndSequence';

export const playMorphMasterGameInstructionsAudioBatch = async (
  baseWord: Word,
  wordToCreate: Word,
  segmentationEnabled: boolean,
  audioDelay: number,
) => {
  await playTurnTheWordAudio();
  await playWordAudio(baseWord.spelling);
  await playIntoAudio();
  await playWordAudio(wordToCreate.spelling);
  if (segmentationEnabled) {
    //const wordMorphemes = getMorphemesByIds(wordToCreate.morphemes);
    await soundOutWord(
      getWordGraphemes(wordToCreate),
      audioDelay,
      getWordGraphemes(wordToCreate),
    );
    await playWordAudio(wordToCreate.spelling);
  }
};

export function getInitialMorphemeOptions(
  baseWordMorphemes: Morpheme[],
  wordToCreateMorphemes: Morpheme[],
  availableMorphemes: Morpheme[],
  distractorCardsEnabled: boolean,
  numberOfDistractors: number = 4,
) {
  const countMorphemes = (morphemes: Morpheme[], id: string) =>
    morphemes.filter((m) => m.id === id).length;

  // Calculate the required letters to transform baseWord into wordToCreate
  const seen = new Set<string>();
  const requiredMorphemes: Morpheme[] = [];
  for (const morpheme of wordToCreateMorphemes) {
    // skip this morpheme if we've already processed it
    if (seen.has(morpheme.id)) continue;
    seen.add(morpheme.id);

    // figure out how many of this morpheme we need
    const numInBaseWord = countMorphemes(baseWordMorphemes, morpheme.id);
    const numInWordToCreate = countMorphemes(
      wordToCreateMorphemes,
      morpheme.id,
    );
    const requiredCount = numInWordToCreate - numInBaseWord;

    // add that many of this morpheme to the required array
    if (requiredCount > 0) {
      requiredMorphemes.push(...Array(requiredCount).fill(morpheme));
    }
  }

  // Filter all word chain garphemes not included in the base word and that are not already in requiredLetters
  const additionalMorphemes = getDistinctMorphemes(
    [...baseWordMorphemes, ...wordToCreateMorphemes],
    availableMorphemes,
  );
  let distractorMorphemes: Morpheme[] = [];
  if (distractorCardsEnabled) {
    const wordHasSpelling = (m: Morpheme) =>
      additionalMorphemes.some((wm: Morpheme) => wm.spelling === m.spelling)
        ? 2.5
        : 1;
    distractorMorphemes = weightedSample(
      additionalMorphemes,
      wordHasSpelling,
      numberOfDistractors,
    );
  }

  // Build the final set of options
  const options = [...requiredMorphemes, ...distractorMorphemes];

  // Shuffle the resulting letter options array for randomness before returning
  return options.sort(() => Math.random() - 0.5);
}
